import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';

/**
 * ResetPassword page for completing a password reset.
 * Validates token and allows user to set a new password.
 */
export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const returnTo = searchParams.get('return_to') || '';

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setError('Missing reset token. Please request a new password reset link.');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(
          `/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`
        );
        const data = await response.json();

        if (response.ok && data.valid) {
          setTokenValid(true);
          setTokenEmail(data.email || '');
        } else {
          setError(data.error || 'Invalid or expired reset link. Please request a new one.');
        }
      } catch (err) {
        setError('Failed to validate reset link. Please try again.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation with toasts
    if (!password) {
      toast.error('Please enter a new password');
      return;
    }

    if (password.length < 10) {
      toast.error('Password must be at least 10 characters');
      setError('Password must be at least 10 characters');
      return;
    }

    if (!confirmPassword) {
      toast.error('Please confirm your password');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.error || 'Failed to reset password';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      toast.success('Password reset successful!');
      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        const loginUrl = returnTo
          ? `/auth/login?continue=${encodeURIComponent(returnTo)}`
          : '/auth/login';
        navigate(loginUrl);
      }, 2000);
    } catch (err) {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div
        className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <img src={logo} alt="LevelUp" className="h-10 mt-8" />
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#25BD71] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div
        className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <img src={logo} alt="LevelUp" className="h-10 mt-8" />
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Invalid reset link
            </h1>
            <p className="text-sm text-gray-500 mb-6">{error}</p>
            <Link
              to="/auth/forgot-password"
              className="inline-block bg-[#25BD71] text-white py-2 px-4 rounded-md font-medium hover:bg-[#1EA35E] transition-colors"
            >
              Request new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div
        className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <img src={logo} alt="LevelUp" className="h-10 mt-8" />
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Password reset successful
            </h1>
            <p className="text-sm text-gray-500">
              Redirecting you to sign in...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Set new password
          </h1>
          {tokenEmail && (
            <p className="text-sm text-gray-500 mt-1">
              for {tokenEmail}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                autoFocus
                minLength={10}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={10}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#25BD71] text-white py-2 px-4 rounded-md font-medium hover:bg-[#1EA35E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Resetting...' : 'Reset password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Password must be at least 10 characters
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
